import { h, Fragment } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { UsageChart, UsageBucket } from '../shared-ui/UsageChart';
import { authFetch } from '../api/authFetch';

interface CascadeTierEntry {
  tier: string;
  costUsd: number;
  inputTokens: number;
  outputTokens: number;
  requests: number;
}

interface CascadeTotals {
  costUsd: number;
  inputTokens: number;
  outputTokens: number;
  requests: number;
  byTier: CascadeTierEntry[];
}

interface ProviderTotal {
  provider: string;
  costUsd: number;
  sessions: number;
}

interface ModelTotal {
  model: string;
  costUsd: number;
  inputTokens: number;
  outputTokens: number;
  messages: number;
}

interface UsageResponse {
  period: string;
  buckets: UsageBucket[];
  totals: {
    costUsd: number;
    sessions: number;
    turns: number;
    byProvider: ProviderTotal[];
    byModel: ModelTotal[];
    cascade?: CascadeTotals;
  };
}

// ── InfoTooltip ──────────────────────────────────────────────────────────────

interface InfoTooltipProps {
  text: string;
}

function InfoTooltip({ text }: InfoTooltipProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [open]);

  return (
    <div class="info-tip" ref={ref}>
      <button
        class="info-tip-btn"
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        aria-label="More information"
        type="button"
      >
        ℹ
      </button>
      {open && (
        <div class="info-tip-popup" role="tooltip">
          <p>{text}</p>
        </div>
      )}
    </div>
  );
}

// ── Metric info strings ──────────────────────────────────────────────────────

const INFO: Record<string, string> = {
  total: 'Combined cost of all AI operations this period — Claude token usage plus any Vertex AI media generation (video, image). Resets at the start of each period.',
  avg: 'Your average spend per day (or week/month bucket) across the selected period. Useful for spotting spend trends.',
  peak: 'The single most expensive day (or week/month bucket) in the period. Spikes often indicate a long Opus session or a batch of video generations.',
  sessions: 'Total conversation sessions started in this period. One session = one continuous conversation thread, which may span many turns.',
  cascade: 'Cost of the automatic multi-model routing pipeline. Haiku handles quick questions (cheapest), Sonnet handles dev tasks, Opus handles deep reasoning. Tracked separately from session totals.',
  anthropic: 'Claude API costs — token-based pricing for Haiku, Sonnet, and Opus. Charged per input + output token at each model\'s rate.',
  google: 'Google / Vertex AI costs — includes Gemini text models AND media generation via MCP tools: Veo 3 video ($0.75/sec), Veo 2 video ($0.35/sec), Imagen images ($0.03/image). A few sessions with video generation can look large here.',
  model: 'Per-model cost breakdown sourced from individual chat messages — covers all cascade calls, not just primary session cost.',
};

function providerInfo(provider: string): string {
  if (provider === 'anthropic') return INFO.anthropic;
  if (provider === 'google') return INFO.google;
  return `Cost attributed to the "${provider}" AI provider for this period.`;
}

function modelInfo(model: string): string {
  if (model.includes('opus')) return 'Claude Opus — most capable, highest cost. Used for deep reasoning, complex architecture, and difficult code. Self-escalates from Sonnet when needed.';
  if (model.includes('sonnet')) return 'Claude Sonnet — primary coding model. Handles most development tasks. Escalates to Opus for hard problems.';
  if (model.includes('haiku')) return 'Claude Haiku — fast and cheap. Handles simple questions, quick lookups, and signpost summaries. Intercepted before reaching Sonnet.';
  if (model.includes('gemini-2.5-pro') || model.includes('gemini-3')) return 'Gemini Pro — Google\'s most capable model. Used for Google-specific tasks, media prompts, and web-knowledge queries.';
  if (model.includes('flash-lite')) return 'Gemini Flash Lite — ultra-cheap Gemini tier for simple classifications and summaries.';
  if (model.includes('flash')) return 'Gemini Flash — fast, low-cost Google model. Used for AKB sync, media prompt crafting, and quick Gemini lookups.';
  if (model.includes('veo')) return 'Veo video generation (Vertex AI). Veo 3 includes AI-generated audio. Cost is per second of video generated — each 8-second clip at $0.75/sec = $6.00.';
  if (model.includes('imagen')) return 'Imagen image generation (Vertex AI). Cost is per image generated ($0.03/image at default rates).';
  if (model.includes('gemma')) return 'Gemma — local open-source model running on your GPU via Ollama. Zero API cost.';
  return `Cost for AI model "${model}" this period.`;
}

// ── MediaPricingSettings ─────────────────────────────────────────────────────

function MediaPricingSettings() {
  const [open, setOpen] = useState(false);
  const [rates, setRates] = useState<Record<string, number> | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!open || rates !== null) return;
    fetch('/api/me/media-rates').then(r => r.json()).then(setRates).catch(() => {});
  }, [open]);

  const save = async () => {
    if (!rates) return;
    setSaving(true);
    await fetch('/api/me/media-rates', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(rates),
    }).catch(() => {});
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const FIELDS: Array<[string, string, string]> = [
    ['veo2PerSecond', 'Veo 2', '$/sec'],
    ['veo3PerSecond', 'Veo 3', '$/sec'],
    ['imagenPerImage', 'Imagen 4', '$/image'],
    ['geminiFlashPerMInputToken', 'Gemini Flash', '$/token'],
    ['geminiProPerMInputToken', 'Gemini Pro', '$/token'],
  ];

  return (
    <div class="usage-media-rates">
      <button class="usage-media-rates-toggle" onClick={() => setOpen(o => !o)}>
        <span>Media Pricing</span>
        <span class={`usage-chevron${open ? ' open' : ''}`}>›</span>
      </button>
      {open && (
        <div class="usage-media-rates-body">
          {!rates ? (
            <p class="usage-media-rates-hint">Loading…</p>
          ) : (
            <>
              <p class="usage-media-rates-hint">Per-unit rates used for Veo/Imagen/Gemini MCP tool cost estimates. Changes apply to future generations only.</p>
              {FIELDS.map(([key, label, unit]) => (
                <div key={key} class="usage-rate-row">
                  <label class="usage-rate-label">{label}</label>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    class="usage-rate-input"
                    value={rates[key] ?? ''}
                    onInput={(e) => setRates(r => ({ ...r!, [key]: parseFloat((e.target as HTMLInputElement).value) || 0 }))}
                  />
                  <span class="usage-rate-unit">{unit}</span>
                </div>
              ))}
              <button class="usage-rate-save" onClick={save} disabled={saving}>
                {saved ? '✓ Saved' : saving ? 'Saving…' : 'Save rates'}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── CascadeBreakdown ─────────────────────────────────────────────────────────

const TIER_LABELS: Record<string, string> = { haiku: 'Haiku', sonnet: 'Sonnet', opus: 'Opus' };
const TIER_ORDER = ['haiku', 'sonnet', 'opus'];

// Approximate list-price rates per 1M tokens: [input, output]
const OPUS_RATES: [number, number] = [15, 75];

interface CascadeBreakdownProps {
  cascade: CascadeTotals;
  fmtCost: (c: number) => string;
  fmtTokens: (t: number) => string;
}

function CascadeBreakdown({ cascade, fmtCost, fmtTokens }: CascadeBreakdownProps) {
  const tiers = cascade.byTier ?? [];
  if (tiers.length === 0) return null;

  const totalIn = tiers.reduce((s, t) => s + (t.inputTokens || 0), 0);
  const totalOut = tiers.reduce((s, t) => s + (t.outputTokens || 0), 0);
  const allOpusCost = (totalIn * OPUS_RATES[0] + totalOut * OPUS_RATES[1]) / 1_000_000;
  const saved = allOpusCost - cascade.costUsd;

  const sorted = [...tiers].sort(
    (a, b) => TIER_ORDER.indexOf(a.tier) - TIER_ORDER.indexOf(b.tier),
  );

  return (
    <div class="usage-provider-section">
      <div class="usage-section-label">
        Cascade Routing
        <InfoTooltip text="Per-tier breakdown of cascade routing costs. Haiku handles quick tasks (cheapest), Sonnet handles dev tasks, Opus handles deep reasoning. Savings vs all-Opus is an estimate using list-price rates." />
        {saved > 0.001 && (
          <span class="usage-section-sub">saved ~{fmtCost(saved)} vs all-Opus</span>
        )}
      </div>
      <div class="usage-summary-cards">
        {sorted.map((t) => (
          <div key={t.tier} class={`usage-card usage-card--cascade usage-card--cascade-${t.tier}`}>
            <div class="usage-card-label">
              <span class={`usage-provider-dot seg-cascade-${t.tier}`} />
              {TIER_LABELS[t.tier] ?? t.tier}
            </div>
            <div class="usage-card-value">{fmtCost(t.costUsd)}</div>
            <div class="usage-card-sub">
              {t.requests} reqs · {fmtTokens((t.inputTokens || 0) + (t.outputTokens || 0))} tok
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── UsageTab ─────────────────────────────────────────────────────────────────

interface Props {
  isAdmin: boolean;
}

type Period = 'day' | 'week' | 'month';

export function UsageTab({ isAdmin }: Props) {
  const [period, setPeriod] = useState<Period>('day');
  const [filterUserId, setFilterUserId] = useState<string>('');
  const [users, setUsers] = useState<Array<{ userId: string; email: string }>>([]);
  const [data, setData] = useState<UsageResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = async () => {
    if (!isAdmin) return;
    try {
      const res = await authFetch('/api/admin/users');
      const list = await res.json();
      setUsers(list.map((u: any) => ({ userId: u.userId, email: u.email || u.userId })));
    } catch {}
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const base = isAdmin ? '/api/admin/usage' : '/api/me/usage';
      const params = new URLSearchParams({ period });
      if (isAdmin && filterUserId) params.set('userId', filterUserId);
      const res = await authFetch(`${base}?${params}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e: any) {
      setError(e.message || 'Failed to load usage data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, [isAdmin]);
  useEffect(() => { fetchData(); }, [period, filterUserId, isAdmin]);

  const fmtCost = (c: number) => c < 0.01 ? `$${c.toFixed(4)}` : `$${c.toFixed(2)}`;
  const fmtTokens = (t: number) => t >= 1_000_000 ? `${(t / 1_000_000).toFixed(1)}M` : t >= 1000 ? `${(t / 1000).toFixed(1)}k` : `${t}`;
  const shortModel = (m: string) => {
    if (m.includes('opus')) return 'Opus';
    if (m.includes('sonnet')) return 'Sonnet';
    if (m.includes('haiku')) return 'Haiku';
    if (m.includes('gemini-3')) return 'Gemini 3 Pro';
    if (m.includes('gemini-2.5-pro')) return 'Gemini 2.5 Pro';
    if (m.includes('gemini-2.5-flash-lite')) return 'Flash Lite';
    if (m.includes('gemini-2.5-flash')) return 'Gemini Flash';
    if (m.includes('imagen')) return 'Imagen';
    if (m.includes('veo')) return 'Veo';
    if (m.includes('gemma')) return 'Gemma';
    return m;
  };

  const mostExpensiveDay = data?.buckets.length
    ? data.buckets.reduce((a, b) => b.totalCostUsd > a.totalCostUsd ? b : a)
    : null;

  const avgPerBucket = data && data.buckets.length > 0
    ? data.totals.costUsd / data.buckets.length
    : 0;

  return (
    <div class="usage-tab">
      <div class="usage-controls">
        <div class="usage-period-pills">
          {(['day', 'week', 'month'] as Period[]).map((p) => (
            <button
              key={p}
              class={`usage-period-pill${period === p ? ' active' : ''}`}
              onClick={() => setPeriod(p)}
            >
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </button>
          ))}
        </div>
        {isAdmin && users.length > 0 && (
          <select
            class="usage-user-filter"
            value={filterUserId}
            onChange={(e) => setFilterUserId((e.target as HTMLSelectElement).value)}
          >
            <option value="">All users</option>
            {users.map((u) => (
              <option key={u.userId} value={u.userId}>{u.email}</option>
            ))}
          </select>
        )}
      </div>

      {data && (
        <Fragment>
        <div class="usage-summary-cards">
          <div class="usage-card">
            <div class="usage-card-label">
              Total ({period})
              <InfoTooltip text={INFO.total} />
            </div>
            <div class="usage-card-value">{fmtCost(data.totals.costUsd)}</div>
          </div>
          <div class="usage-card">
            <div class="usage-card-label">
              Avg / {period}
              <InfoTooltip text={INFO.avg} />
            </div>
            <div class="usage-card-value">{fmtCost(avgPerBucket)}</div>
          </div>
          <div class="usage-card">
            <div class="usage-card-label">
              Peak {period}
              <InfoTooltip text={INFO.peak} />
            </div>
            <div class="usage-card-value">
              {mostExpensiveDay ? fmtCost(mostExpensiveDay.totalCostUsd) : '—'}
            </div>
          </div>
          <div class="usage-card">
            <div class="usage-card-label">
              Sessions
              <InfoTooltip text={INFO.sessions} />
            </div>
            <div class="usage-card-value">{data.totals.sessions}</div>
          </div>
          {data.totals.cascade && data.totals.cascade.requests > 0 && (
            <div class="usage-card">
              <div class="usage-card-label">
                Cascade
                <InfoTooltip text={INFO.cascade} />
              </div>
              <div class="usage-card-value">{fmtCost(data.totals.cascade.costUsd)}</div>
              <div class="usage-card-sub">{data.totals.cascade.requests} reqs</div>
            </div>
          )}
        </div>

        {/* Cascade routing breakdown */}
        {data.totals.cascade && data.totals.cascade.requests > 0 && (
          <CascadeBreakdown cascade={data.totals.cascade} fmtCost={fmtCost} fmtTokens={fmtTokens} />
        )}

        {/* Provider breakdown */}
        {data.totals.byProvider && data.totals.byProvider.length > 0 && (
          <div class="usage-provider-section">
            <div class="usage-section-label">
              By Provider
              <InfoTooltip text="Session-level costs reported directly by each AI provider's SDK. Sums to the 'Total' figure above. This is the most accurate cost figure." />
              <span class="usage-section-sub">SDK-reported · matches Total</span>
            </div>
            <div class="usage-summary-cards">
              {data.totals.byProvider.map((p) => (
                <div key={p.provider} class={`usage-card usage-card--provider usage-card--${p.provider}`}>
                  <div class="usage-card-label">
                    <span class={`usage-provider-dot seg-${p.provider}`} />
                    {p.provider === 'anthropic' ? 'Anthropic' : p.provider === 'google' ? 'Google / Vertex' : p.provider}
                    <InfoTooltip text={providerInfo(p.provider)} />
                  </div>
                  <div class="usage-card-value">{fmtCost(p.costUsd)}</div>
                  <div class="usage-card-sub">{p.sessions} sessions</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Per-model cost breakdown — from chat_message (includes cascade roles) */}
        {data.totals.byModel && data.totals.byModel.length > 0 && (
          <div class="usage-provider-section">
            <div class="usage-section-label">
              By Model
              <InfoTooltip text="Estimated costs per AI model, computed from individual message token counts at API list rates. Includes all cascade routing calls (Haiku signposts, Sonnet→Opus escalations). Independent tracking system — will not match 'By Provider' totals." />
              <span class="usage-section-sub">estimated · independent of Total</span>
            </div>
            <div class="usage-summary-cards">
              {[...data.totals.byModel].sort((a, b) => b.costUsd - a.costUsd).map((m) => (
                <div key={m.model} class="usage-card usage-card--model">
                  <div class="usage-card-label">
                    {shortModel(m.model)}
                    <InfoTooltip text={modelInfo(m.model)} />
                  </div>
                  <div class="usage-card-value">{fmtCost(m.costUsd)}</div>
                  <div class="usage-card-sub">{m.messages} msgs · {fmtTokens(m.inputTokens + m.outputTokens)} tok</div>
                </div>
              ))}
            </div>
          </div>
        )}
        </Fragment>
      )}

      {loading && <div class="usage-loading">Loading…</div>}
      {error && <div class="usage-error">{error}</div>}
      {!loading && data && <UsageChart buckets={data.buckets} period={period} />}
      <MediaPricingSettings />
    </div>
  );
}
